/**
 * Wave 1B — Details live registry / orchestration helpers.
 * Presentation never carries rawDeal. Reprice stays Results-owned.
 */

import { mapFlightOfferViewModel } from "../view-models";
import { mapBookingPriceViewModel } from "../view-models/mappers/mapBookingPriceViewModel";
import { buildGreenfieldSearchViewModel } from "./buildGreenfieldSearchViewModel";
import { buildFlightDetailsViewModel } from "./buildFlightDetailsViewModel";
import { toGreenfieldDetailsOffer } from "./toGreenfieldDetailsOffer";

/** Live sticky CTA — truthful; next step may be extras (Wave 2 owns Passengers). */
export const DETAILS_LIVE_CONTINUE_LABEL = "המשך";

/**
 * Create-quote secondary action has no approved placement in greenfield StickyBookingSummary.
 * Capability is retained as a data contract only until design provides a slot.
 */
export const QUOTE_ACTION_UI_GAP = "QUOTE_ACTION_UI_GAP";

/**
 * Build greenfield Details props from existing booking state.
 * Bridge-private: may read selectedFlightOffer (incl. rawDeal) only while mapping VMs.
 *
 * @param {{
 *   selectedFlightOffer: object|null,
 *   pricing?: object|null,
 *   bookingExchangeRateSnapshot?: number|null,
 *   extrasSelections?: object|null,
 *   searchFields?: object|null,
 *   handleFeeData?: { type?: string, value?: number }|null,
 *   canAdvance?: boolean,
 * }} input
 */
export function buildDetailsWave1bPresentation(input = {}) {
  const offer = input.selectedFlightOffer || null;
  if (!offer) {
    return {
      offer: null,
      search: buildSearchVm(input.searchFields),
      offerVm: null,
      detailsVm: null,
      priceVm: null,
      handlingFee: buildHandlingFeeContract(input.handleFeeData, input.pricing),
      quote: {
        canCreateQuote: false,
        gap: QUOTE_ACTION_UI_GAP,
      },
      continueLabel: DETAILS_LIVE_CONTINUE_LABEL,
      showReprice: false,
    };
  }

  const offerVm = mapFlightOfferViewModel(offer, {
    pricing: input.pricing ?? null,
    bookingExchangeRateSnapshot: input.bookingExchangeRateSnapshot ?? null,
    extrasSelections: input.extrasSelections ?? null,
  });

  const priceVm =
    offerVm?.price ||
    mapBookingPriceViewModel({
      offer,
      pricing: input.pricing ?? null,
      bookingExchangeRateSnapshot: input.bookingExchangeRateSnapshot ?? null,
    });

  const detailsVm = buildFlightDetailsViewModel({ offerVm, priceVm });
  const search = buildSearchVm(input.searchFields);

  const greenfieldOffer = toGreenfieldDetailsOffer({
    offerVm,
    detailsVm,
    priceVm,
    searchVm: search,
  });

  return {
    offer: greenfieldOffer,
    search,
    offerVm,
    detailsVm,
    priceVm,
    handlingFee: buildHandlingFeeContract(input.handleFeeData, input.pricing),
    quote: {
      // Capability retained; UI placement is a documented GAP (DESIGN FREEZE).
      canCreateQuote: input.canAdvance !== false && Boolean(offerVm?.id),
      gap: QUOTE_ACTION_UI_GAP,
    },
    continueLabel: DETAILS_LIVE_CONTINUE_LABEL,
    showReprice: false,
  };
}

/**
 * Greenfield fee control → existing handlers. No new pricing math.
 * @param {{ updateHandleFeeData: Function, recomputePricingNow: Function }} handlers
 * @param {{ type?: string, value?: number }} payload
 */
export function applyHandlingFeeChange(handlers, payload = {}) {
  const type = payload?.type === "%" ? "%" : "+";
  const value = Number.isFinite(Number(payload?.value))
    ? Number(payload.value)
    : 0;
  handlers.updateHandleFeeData(type, value);
  handlers.recomputePricingNow({ handleFeeData: { type, value } });
}

/**
 * Quote stays on createDraftFromFlightDetails. No greenfield button (QUOTE_ACTION_UI_GAP).
 * @param {{ canCreateQuote?: boolean, createDraftFromFlightDetails?: Function }} input
 */
export function bindDetailsQuoteAction(input = {}) {
  if (!input.canCreateQuote || typeof input.createDraftFromFlightDetails !== "function") {
    return null;
  }
  return () => input.createDraftFromFlightDetails();
}

export function buildHandlingFeeContract(handleFeeData, pricing) {
  const type =
    handleFeeData?.type === "%" || handleFeeData?.type === "+"
      ? handleFeeData.type
      : "+";
  const value = Number.isFinite(Number(handleFeeData?.value))
    ? Number(handleFeeData.value)
    : 0;
  return {
    visible: true,
    type,
    value,
    options: [
      { type: "+", label: "₪" },
      { type: "%", label: "%" },
    ],
    ourPricePreview: pricing?.managedOurTotalFare ?? null,
  };
}

function buildSearchVm(searchFields) {
  if (!searchFields) {
    return buildGreenfieldSearchViewModel({});
  }
  return buildGreenfieldSearchViewModel(searchFields);
}
